import { orderTeams } from "./internal/tiebreak.js";
/** Derive both teams' lines from a counted pool game (§7.1, §7.6). */
function gameLines(g, config) {
    if (g.homeTeamId === undefined || g.awayTeamId === undefined)
        return null;
    if (g.status === "final") {
        if (g.homeScore === undefined || g.awayScore === undefined)
            return null;
        const h = g.homeScore;
        const a = g.awayScore;
        const homeOutcome = h > a ? "W" : h < a ? "L" : "T";
        const awayOutcome = h > a ? "L" : h < a ? "W" : "T";
        return [
            { teamId: g.homeTeamId, scored: h, allowed: a, outcome: homeOutcome },
            { teamId: g.awayTeamId, scored: a, allowed: h, outcome: awayOutcome },
        ];
    }
    if (g.status === "forfeit") {
        if (!g.forfeitTeamId)
            return null;
        const { winner, loser } = config.forfeitScore;
        const homeForfeited = g.forfeitTeamId === g.homeTeamId;
        const homeScored = homeForfeited ? loser : winner;
        const awayScored = homeForfeited ? winner : loser;
        return [
            {
                teamId: g.homeTeamId,
                scored: homeScored,
                allowed: awayScored,
                outcome: homeForfeited ? "L" : "W",
            },
            {
                teamId: g.awayTeamId,
                scored: awayScored,
                allowed: homeScored,
                outcome: homeForfeited ? "W" : "L",
            },
        ];
    }
    /* v8 ignore next */
    return null;
}
/**
 * Compute per-pool standings (spec §7.1–§7.7). Only counted pool games
 * (status ∈ {final, forfeit}) count; teams with poolId null are excluded.
 * Rows are ordered by winPct then the tiebreaker chain, with ranks assigned
 * by final position and unresolved ties flagged.
 */
export function computeStandings(teams, games, config) {
    const margin = config.runCapEnabled ? config.runCapMargin : Number.POSITIVE_INFINITY;
    // Accumulators only for teams assigned to a pool; track pool encounter order.
    const acc = new Map();
    const poolOrder = [];
    const seenPools = new Set();
    for (const team of teams) {
        if (team.poolId === null)
            continue;
        acc.set(team.id, { team, w: 0, l: 0, t: 0, gp: 0, rs: 0, ra: 0, crs: 0, cra: 0 });
        if (!seenPools.has(team.poolId)) {
            seenPools.add(team.poolId);
            poolOrder.push(team.poolId);
        }
    }
    const countedPool = games.filter((g) => g.type === "pool" && (g.status === "final" || g.status === "forfeit"));
    for (const g of countedPool) {
        const lines = gameLines(g, config);
        if (!lines)
            continue;
        for (const ln of lines) {
            const A = acc.get(ln.teamId);
            if (!A)
                continue;
            A.gp++;
            if (ln.outcome === "W")
                A.w++;
            else if (ln.outcome === "L")
                A.l++;
            else
                A.t++;
            A.rs += ln.scored;
            A.ra += ln.allowed;
            A.crs += Math.min(ln.scored, ln.allowed + margin);
            A.cra += Math.min(ln.allowed, ln.scored + margin);
        }
    }
    // Build rows and group by pool.
    const rowsByPool = new Map();
    for (const A of acc.values()) {
        const poolId = A.team.poolId;
        const winPct = A.gp > 0 ? (A.w + 0.5 * A.t) / A.gp : null;
        const row = {
            teamId: A.team.id,
            teamName: A.team.name,
            poolId,
            wins: A.w,
            losses: A.l,
            ties: A.t,
            gamesPlayed: A.gp,
            winPct,
            runsScored: A.rs,
            runsAllowed: A.ra,
            countedRunsScored: A.crs,
            countedRunsAllowed: A.cra,
            avgCountedRunsScored: A.gp > 0 ? A.crs / A.gp : null,
            avgCountedRunsAllowed: A.gp > 0 ? A.cra / A.gp : null,
            rank: 0,
            unresolved: false,
        };
        const bucket = rowsByPool.get(poolId);
        if (bucket)
            bucket.push(row);
        else
            rowsByPool.set(poolId, [row]);
    }
    const result = [];
    for (const poolId of poolOrder) {
        const rows = rowsByPool.get(poolId) ?? [];
        const countedForPool = countedPool.filter((g) => g.poolId === poolId);
        const { ordered } = orderTeams(rows, countedForPool);
        ordered.forEach((r, i) => {
            r.rank = i + 1;
        });
        result.push({ poolId, rows: ordered });
    }
    return result;
}
//# sourceMappingURL=standings.js.map